import React, { useState, useEffect } from "react";
import ConfirmModal from "../components/ConfirmModal";
import { classifyProvisionError } from "../utils/provisionError";

const API = process.env.REACT_APP_API_URL || "";

// New VM types added 2026-10-08 — SA9 (Bangkok, Jakarta) + SA5 (Hong Kong),
// all 16 core / 64GiB. Kept on a separate page (not the main Tencent.jsx
// Manual Instances panel) per request, but same design language.
//
// /tencent/list returns the RAW Tencent InstanceType string (e.g.
// "SA9.4XLARGE64"), not our internal instance_type key — and Bangkok-SA9
// and Jakarta-SA9 share that same raw type, so matching needs BOTH the
// raw type AND the region to tell them apart.
const CART_ITEMS = [
  {
    key: "bk_spot_sa9_16_64",
    name: "Bangkok — SA9",
    region: "🇹🇭 Asia Pacific (Bangkok)",
    apiRegion: "ap-bangkok",
    model: "CVM Standard SA9",
    realType: "SA9.4XLARGE64",
    spec: "16 Core / 64 GiB",
    color: "#f472b6",
    ready: true,
  },
  {
    key: "jk_spot_sa9_16_64",
    name: "Jakarta — SA9",
    region: "🇮🇩 Asia Pacific (Jakarta)",
    apiRegion: "ap-jakarta",
    model: "CVM Standard SA9",
    realType: "SA9.4XLARGE64",
    spec: "16 Core / 64 GiB",
    color: "#fbbf24",
    ready: true,
  },
  {
    key: "hk_spot_sa5_16_64",
    name: "Hong Kong — SA5",
    region: "🇭🇰 China (Hong Kong)",
    apiRegion: "ap-hongkong",
    model: "CVM Standard SA5",
    realType: "SA5.4XLARGE64",
    spec: "16 Core / 64 GiB",
    color: "#38bdf8",
    ready: true,
  },
];
const cartMetaFor = (inst) =>
  CART_ITEMS.find(c => c.realType === inst.instance_type && c.apiRegion === inst.region);

const StatusBadge = ({ status }) => {
  const map = {
    RUNNING:  { cls: "adm-badge adm-badge-green", label: "● Running" },
    PENDING:  { cls: "adm-badge adm-badge-amber", label: "◌ Pending" },
    STOPPED:  { cls: "adm-badge adm-badge-red",   label: "○ Stopped" },
    STOPPING: { cls: "adm-badge adm-badge-amber", label: "◌ Stopping" },
  };
  const s = map[status] || { cls: "adm-badge adm-badge-gray", label: status };
  return <span className={s.cls}>{s.label}</span>;
};

const STAGE_LABELS = {
  booting:             "Booting instance...",
  installing_deps:     "Installing dependencies...",
  installing_chromium: "Installing Chromium...",
  starting_service:    "Starting agent service...",
  online:              "Online",
  failed:              "Install failed",
};

const ProvisionBar = ({ stage, pct, error }) => {
  if (!stage || stage === "online") return null;

  const isFailed = stage === "failed";
  const barPct   = isFailed ? 100 : (pct || 0);
  const label    = STAGE_LABELS[stage] || stage;

  return (
    <div style={{ marginBottom: 12 }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 5 }}>
        <span style={{ fontSize: 11, color: isFailed ? "#f87171" : "#94a3b8", fontWeight: 600 }}>{label}</span>
        {!isFailed && <span style={{ fontSize: 11, color: "#4a6280", fontWeight: 700 }}>{barPct}%</span>}
      </div>
      <div style={{ height: 5, borderRadius: 99, background: "rgba(255,255,255,0.07)", overflow: "hidden" }}>
        <div style={{
          height: "100%", borderRadius: 99, width: `${barPct}%`,
          background: isFailed
            ? "linear-gradient(90deg,#ef4444,#b91c1c)"
            : "linear-gradient(90deg,#38bdf8,#6366f1)",
          transition: "width .6s ease",
          boxShadow: isFailed ? "0 0 8px #ef444466" : "0 0 8px #38bdf866",
        }} />
      </div>
      {isFailed && error && (
        <div style={{
          marginTop: 8, background: "rgba(127,29,29,0.35)",
          border: "1px solid rgba(239,68,68,0.3)", borderRadius: 8,
          padding: "8px 10px", fontSize: 11, color: "#fca5a5",
          fontFamily: "'JetBrains Mono', monospace", wordBreak: "break-word", lineHeight: 1.5,
        }}>
          {classifyProvisionError(error) && (
            <div style={{ fontWeight: 700, marginBottom: 4, fontFamily: "inherit" }}>
              {classifyProvisionError(error).label}
            </div>
          )}
          {error}
        </div>
      )}
    </div>
  );
};

const AccountBadge = ({ label }) => {
  if (!label) return null;
  const palette = [
    { bg: "rgba(56,189,248,0.12)",  border: "rgba(56,189,248,0.35)",  color: "#38bdf8" },
    { bg: "rgba(99,102,241,0.12)",  border: "rgba(99,102,241,0.35)",  color: "#818cf8" },
    { bg: "rgba(34,197,94,0.12)",   border: "rgba(34,197,94,0.35)",   color: "#4ade80" },
    { bg: "rgba(251,191,36,0.12)",  border: "rgba(251,191,36,0.35)",  color: "#fbbf24" },
    { bg: "rgba(244,114,182,0.12)", border: "rgba(244,114,182,0.35)", color: "#f472b6" },
    { bg: "rgba(20,184,166,0.12)",  border: "rgba(20,184,166,0.35)",  color: "#2dd4bf" },
    { bg: "rgba(168,85,247,0.12)",  border: "rgba(168,85,247,0.35)",  color: "#c084fc" },
  ];
  const num   = parseInt((label.match(/\d+$/) || ["1"])[0], 10);
  const style = palette[(num - 1) % palette.length];
  return (
    <span style={{
      fontSize: 10, fontWeight: 800, letterSpacing: "0.8px",
      textTransform: "uppercase", padding: "2px 8px", borderRadius: 99,
      background: style.bg, border: `1px solid ${style.border}`, color: style.color,
      fontFamily: "'Outfit', sans-serif",
    }}>
      {label}
    </span>
  );
};

export default function Tencent80() {
  const [instances, setInstances]     = useState([]);
  const [loading, setLoading]         = useState(false);
  const [selected, setSelected]       = useState(new Set());
  const [toast, setToast]             = useState(null);
  const [deletingIds, setDeletingIds] = useState(new Set());
  const [retryingIds, setRetryingIds] = useState(new Set());
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [confirmSelected, setConfirmSelected] = useState(false);
  const [confirmAll, setConfirmAll]   = useState(false);
  const [cartType, setCartType]       = useState(CART_ITEMS[0].key);
  const [count, setCount]             = useState("1");
  const [creating, setCreating]       = useState(false);

  const token = () => localStorage.getItem("token") || "";
  const showToast = (msg, type = "success") => {
    setToast({ msg, type });
    setTimeout(() => setToast(null), 3500);
  };

  const handleRetry = async (instanceId) => {
    setRetryingIds(p => new Set(p).add(instanceId));
    try {
      const r = await fetch(`${API}/tencent/retry/${instanceId}`, {
        method: "POST",
        headers: { Authorization: "Bearer " + token() }
      });
      const d = await r.json();
      if (d.success) {
        showToast("Retry started — re-attempting install over SSH");
        loadInstances(false);
      } else {
        showToast(d.detail || "Retry failed", "error");
      }
    } catch {
      showToast("Network error", "error");
    } finally {
      setRetryingIds(p => { const n = new Set(p); n.delete(instanceId); return n; });
    }
  };

  const loadInstances = async (showLoader = true) => {
    if (showLoader) setLoading(true);
    try {
      const r = await fetch(`${API}/tencent/list`, {
        headers: { Authorization: "Bearer " + token() },
      });
      const d = await r.json();
      const mine = (d.instances || []).filter(i => cartMetaFor(i));
      setInstances(mine);
    } catch {
      showToast("Network error loading instances", "error");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadInstances();
    const t = setInterval(() => loadInstances(false), 15000);
    return () => clearInterval(t);
  }, []);

  const runningCount = instances.filter(i => i.status === "RUNNING").length;
  const pendingCount = instances.filter(i => i.status === "PENDING").length;
  const cartItem = CART_ITEMS.find(c => c.key === cartType);

  const toggleSelect = (id) => {
    setSelected(prev => {
      const s = new Set(prev);
      s.has(id) ? s.delete(id) : s.add(id);
      return s;
    });
  };

  const handleCreate = async () => {
    const n = parseInt(count, 10);
    if (!n || n < 1) { showToast("Enter a valid count", "error"); return; }
    if (!cartItem.ready) { showToast("Ye type abhi ready nahi hai — security group pending hai", "error"); return; }
    setCreating(true);
    try {
      const r = await fetch(`${API}/tencent/create-manual`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: "Bearer " + token() },
        body: JSON.stringify({ count: n, instance_type: cartType, name_prefix: "BotCrowd-Linux" }),
      });
      const d = await r.json();
      if (d.success) {
        showToast(`✅ ${d.count} instance(s) [${cartItem.name}] created! Online in ~2 mins.`);
        await loadInstances();
      } else {
        showToast(d.detail || d.message || "Creation failed", "error");
      }
    } catch {
      showToast("Network error", "error");
    } finally {
      setCreating(false);
    }
  };

  const handleDeleteOne = async (id) => {
    setConfirmDelete(null);
    setDeletingIds(prev => new Set([...prev, id]));
    try {
      const r = await fetch(`${API}/tencent/delete`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: "Bearer " + token() },
        body: JSON.stringify({ instance_ids: [id] }),
      });
      const d = await r.json();
      if (!d.success) {
        showToast(d.detail || "Delete failed", "error");
        await loadInstances(false);
      } else {
        showToast("Instance terminated");
        setInstances(prev => prev.filter(i => i.instance_id !== id));
        setSelected(prev => { const s = new Set(prev); s.delete(id); return s; });
      }
    } catch {
      showToast("Delete failed", "error");
    } finally {
      setDeletingIds(prev => { const s = new Set(prev); s.delete(id); return s; });
    }
  };

  const handleDeleteSelected = async () => {
    setConfirmSelected(false);
    const ids = Array.from(selected);
    setDeletingIds(prev => new Set([...prev, ...ids]));
    try {
      const r = await fetch(`${API}/tencent/delete`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: "Bearer " + token() },
        body: JSON.stringify({ instance_ids: ids }),
      });
      const d = await r.json();
      if (d.success) {
        showToast(`${ids.length} instance(s) terminated`);
        setInstances(prev => prev.filter(i => !ids.includes(i.instance_id)));
        setSelected(new Set());
      } else {
        showToast(d.detail || "Delete failed", "error");
      }
    } catch {
      showToast("Delete failed", "error");
    } finally {
      await loadInstances(false);
      setDeletingIds(new Set());
    }
  };

  const handleDeleteAll = async () => {
    setConfirmAll(false);
    const ids = instances.map(i => i.instance_id);
    setDeletingIds(new Set(ids));
    try {
      const r = await fetch(`${API}/tencent/delete`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: "Bearer " + token() },
        body: JSON.stringify({ instance_ids: ids }),
      });
      const d = await r.json();
      if (d.success) {
        showToast(`${ids.length} instance(s) terminated`);
        setInstances([]);
        setSelected(new Set());
      } else {
        showToast(d.detail || "Delete failed", "error");
      }
    } catch {
      showToast("Delete failed", "error");
    } finally {
      await loadInstances(false);
      setDeletingIds(new Set());
    }
  };

  return (
    <div style={{ animation: "fadeIn .2s ease" }}>
      <style>{`
        .tencent-grid { display: grid; grid-template-columns: repeat(auto-fill, minmax(300px, 1fr)); gap: 14px; }
        .inst-card { background: rgba(8,13,32,0.72); backdrop-filter: blur(28px) saturate(1.5); border: 1px solid rgba(255,255,255,0.09); border-radius: 16px; padding: 18px; box-shadow: 0 8px 48px rgba(0,0,0,0.6), inset 0 1px 0 rgba(255,255,255,0.06); transition: border-color .25s, box-shadow .25s, transform .2s; }
        .inst-card:hover { border-color: rgba(99,179,237,0.2); box-shadow: 0 14px 58px rgba(0,0,0,.65), 0 0 0 1px rgba(99,179,237,0.08), inset 0 1px 0 rgba(255,255,255,.06); }
        .inst-card.selected { border-color: rgba(56,189,248,0.45); background: rgba(56,189,248,0.05); }
        .inst-meta-label { font-size: 10px; color: #334155; text-transform: uppercase; letter-spacing: 1px; margin-bottom: 3px; font-weight: 700; }
        .inst-ip { font-family: 'JetBrains Mono', monospace; font-size: 13px; color: #38bdf8; font-weight: 600; }
        .inst-id { font-family: 'JetBrains Mono', monospace; font-size: 11px; color: #4a6280; }
        .mini-stat { background: rgba(8,13,32,0.72); backdrop-filter: blur(28px) saturate(1.5); border: 1px solid rgba(255,255,255,0.09); border-radius: 14px; padding: 14px 18px; flex: 1; min-width: 110px; }
        .mini-stat-val { font-size: 26px; font-weight: 900; letter-spacing: -1px; line-height: 1; margin-bottom: 4px; }
        .mini-stat-lbl { font-size: 10px; letter-spacing: 1.4px; text-transform: uppercase; font-weight: 800; color: #4a6280; }
        @media(max-width:600px) {
          .tencent-grid { grid-template-columns: 1fr; }
          .mini-stat-row { flex-wrap: wrap; }
        }
      `}</style>

      {toast && (
        <div style={{ position: "fixed", top: 20, right: 16, zIndex: 9999, background: toast.type === "error" ? "rgba(127,29,29,0.97)" : "rgba(20,83,45,0.97)", border: `1px solid ${toast.type === "error" ? "#ef4444" : "#22c55e"}`, color: "#fff", borderRadius: 12, padding: "12px 20px", boxShadow: "0 4px 24px #0009", fontSize: 14, fontWeight: 600, backdropFilter: "blur(16px)", maxWidth: "calc(100vw - 32px)" }}>{toast.msg}</div>
      )}

      {confirmAll && (
        <div className="adm-modal-bg open" onClick={() => setConfirmAll(false)}>
          <div className="adm-modal" onClick={e => e.stopPropagation()}>
            <div className="adm-modal-head">
              <span className="adm-modal-title">Delete All Instances?</span>
              <button className="adm-btn adm-btn-ghost adm-btn-sm" onClick={() => setConfirmAll(false)}>X</button>
            </div>
            <p style={{ color: "#94a3b8", fontSize: 14, lineHeight: 1.6 }}>
              This will permanently terminate <strong style={{ color: "#f87171" }}>ALL {instances.length} instance(s)</strong> on this page. This cannot be undone.
            </p>
            <div className="adm-modal-footer">
              <button className="adm-btn" onClick={() => setConfirmAll(false)}>Cancel</button>
              <button className="adm-btn adm-btn-danger" onClick={handleDeleteAll}>Delete All</button>
            </div>
          </div>
        </div>
      )}

      <div style={{ marginBottom: 20, display: "flex", alignItems: "flex-start", justifyContent: "space-between", flexWrap: "wrap", gap: 10 }}>
        <div>
          <h2 style={{ fontSize: 20, fontWeight: 800, color: "#f1f5f9", margin: 0 }}>Tencent80</h2>
          <p style={{ color: "#4a6280", fontSize: 13, margin: "4px 0 0" }}>Bangkok SA9 · Jakarta SA9 · Hong Kong SA5 · 16 cores 64GB · Spot</p>
        </div>
        <button className="adm-btn adm-btn-sm" onClick={() => loadInstances()} disabled={loading}>{loading ? "..." : "Refresh"}</button>
      </div>

      <div className="mini-stat-row" style={{ display: "flex", gap: 14, marginBottom: 20 }}>
        <div className="mini-stat">
          <div className="mini-stat-val" style={{ color: "#f1f5f9" }}>{instances.length}</div>
          <div className="mini-stat-lbl">Total Instances</div>
        </div>
        <div className="mini-stat">
          <div className="mini-stat-val" style={{ color: "#4ade80" }}>{runningCount}</div>
          <div className="mini-stat-lbl">Running</div>
        </div>
        <div className="mini-stat">
          <div className="mini-stat-val" style={{ color: "#fbbf24" }}>{pendingCount}</div>
          <div className="mini-stat-lbl">Pending</div>
        </div>
      </div>

      {/* Cart / manual create */}
      <div className="adm-card adm-card-pad" style={{ marginBottom: 20 }}>
        <div style={{ fontSize: 13, fontWeight: 700, color: "#cbd5e1", marginBottom: 4, textTransform: "uppercase", letterSpacing: "1px" }}>VM Cart</div>
        <div style={{ fontSize: 12, color: "#4a6280", marginBottom: 14 }}>Naye SA9 / SA5 model VMs — Bangkok, Jakarta, Hong Kong</div>
        <div style={{ display: "flex", gap: 10, flexWrap: "wrap", marginBottom: 18 }}>
          {CART_ITEMS.map(item => (
            <button
              key={item.key}
              onClick={() => setCartType(item.key)}
              disabled={!item.ready}
              style={{
                flex: "1 1 240px", textAlign: "left", padding: "14px 16px", borderRadius: 12,
                cursor: item.ready ? "pointer" : "not-allowed", opacity: item.ready ? 1 : 0.55,
                border: `1px solid ${cartType === item.key ? item.color : "rgba(255,255,255,0.1)"}`,
                background: cartType === item.key ? `${item.color}18` : "rgba(255,255,255,0.04)",
              }}
            >
              <div style={{ fontSize: 14, fontWeight: 700, color: cartType === item.key ? item.color : "#e2e8f0" }}>{item.name}</div>
              <div style={{ fontSize: 12, color: "#94a3b8", marginTop: 4 }}>{item.region}</div>
              <div style={{ fontSize: 12, color: "#64748b", marginTop: 2 }}>{item.model} · {item.realType}</div>
              <div style={{ fontSize: 12, color: "#64748b" }}>{item.spec}</div>
              {!item.ready && <div style={{ fontSize: 11, color: "#f59e0b", marginTop: 6, fontWeight: 600 }}>⏳ Security group pending</div>}
            </button>
          ))}
        </div>
        <div style={{ display: "flex", alignItems: "flex-end", gap: 16, flexWrap: "wrap" }}>
          <div>
            <label className="adm-label">Count</label>
            <input
              type="number" min="1" value={count} onChange={e => setCount(e.target.value)}
              className="count-field"
              style={{ width: 110, background: "rgba(255,255,255,0.06)", border: "1px solid rgba(255,255,255,0.13)", color: "#e2e8f0", borderRadius: 9, padding: "9px 13px", fontSize: 15, fontWeight: 700, fontFamily: "'Outfit', sans-serif", outline: "none" }}
            />
          </div>
          <button className="adm-btn adm-btn-primary" onClick={handleCreate} disabled={creating || !cartItem.ready}>
            {creating ? "Creating…" : `Create ${cartItem.name}`}
          </button>
        </div>
      </div>

      {(selected.size > 0) && (
        <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 14 }}>
          <button className="adm-btn adm-btn-danger adm-btn-sm" onClick={() => setConfirmSelected(true)}>Delete Selected ({selected.size})</button>
          <button className="adm-btn adm-btn-danger adm-btn-sm" style={{ marginLeft: "auto" }} onClick={() => setConfirmAll(true)}>Delete All</button>
        </div>
      )}

      {loading && instances.length === 0 ? (
        <div className="adm-card adm-card-pad adm-empty"><div style={{ fontSize: 28, marginBottom: 8 }}>...</div>Loading instances...</div>
      ) : instances.length === 0 ? (
        <div className="adm-card adm-card-pad adm-empty"><div style={{ fontSize: 32, marginBottom: 8 }}>☁️</div>Koi instance nahi hai abhi. Upar se create karo.</div>
      ) : (
        <div className="tencent-grid">
          {instances.map(inst => {
            const meta = cartMetaFor(inst);
            return (
              <div key={inst.instance_id} className={`inst-card${selected.has(inst.instance_id) ? " selected" : ""}`}>
                <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 8 }}>
                  <label style={{ display: "flex", alignItems: "center", gap: 8, cursor: "pointer" }}>
                    <input type="checkbox" checked={selected.has(inst.instance_id)} onChange={() => toggleSelect(inst.instance_id)} style={{ width: 15, height: 15, cursor: "pointer" }} />
                    <span style={{ fontSize: 13, fontWeight: 700, color: "#e2e8f0" }}>{inst.name}</span>
                  </label>
                  <StatusBadge status={inst.status} />
                </div>
                <div style={{ marginBottom: 12, display: "flex", gap: 8, flexWrap: "wrap" }}>
                  <AccountBadge label={inst.account_label} />
                  <span className="adm-badge adm-badge-gray">{meta?.region || inst.region}</span>
                </div>
                <ProvisionBar stage={inst.provision_stage} pct={inst.provision_pct} error={inst.provision_error} />
                <div style={{ marginBottom: 10 }}>
                  <div className="inst-meta-label">Public IP</div>
                  {inst.public_ip ? <span className="inst-ip">{inst.public_ip}</span> : <span style={{ color: "#4a6280", fontSize: 13 }}>Pending...</span>}
                </div>
                <div style={{ marginBottom: 10 }}>
                  <div className="inst-meta-label">Instance ID</div>
                  <span className="inst-id">{inst.instance_id}</span>
                </div>
                <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginBottom: 14 }}>
                  <span className="adm-badge" style={{ background: `${(meta?.color || "#38bdf8")}26`, color: meta?.color || "#38bdf8", border: `1px solid ${(meta?.color || "#38bdf8")}4d` }}>
                    {meta?.name || inst.instance_type}
                  </span>
                  {inst.created_time && <span className="adm-badge adm-badge-gray">{new Date(inst.created_time).toLocaleDateString()}</span>}
                </div>
                {inst.provision_error && (
                  <button
                    className="adm-btn adm-btn-sm"
                    style={{ width: "100%", justifyContent: "center", marginBottom: 8 }}
                    onClick={() => handleRetry(inst.instance_id)}
                    disabled={retryingIds.has(inst.instance_id)}
                  >
                    {retryingIds.has(inst.instance_id) ? "Retrying…" : "↻ Retry"}
                  </button>
                )}
                <button
                  className="adm-btn adm-btn-danger adm-btn-sm"
                  style={{ width: "100%", justifyContent: "center" }}
                  onClick={() => setConfirmDelete({ id: inst.instance_id, name: inst.name })}
                  disabled={deletingIds.has(inst.instance_id)}
                >
                  {deletingIds.has(inst.instance_id) ? "Deleting..." : "Terminate Instance"}
                </button>
              </div>
            );
          })}
        </div>
      )}

      <ConfirmModal
        open={!!confirmDelete}
        title="Terminate Instance?"
        message={<>Are you sure you want to terminate <strong style={{ color: "#f87171" }}>{confirmDelete?.name || confirmDelete?.id}</strong>? This cannot be undone.</>}
        confirmLabel="Terminate"
        onConfirm={() => handleDeleteOne(confirmDelete?.id)}
        onCancel={() => setConfirmDelete(null)}
      />
      <ConfirmModal
        open={confirmSelected}
        title={`Delete ${selected.size} Instance(s)?`}
        message={<>This will permanently terminate <strong style={{ color: "#f87171" }}>{selected.size} selected instance(s)</strong>. This cannot be undone.</>}
        confirmLabel="Delete Selected"
        onConfirm={handleDeleteSelected}
        onCancel={() => setConfirmSelected(false)}
      />
    </div>
  );
}
